import { useUser } from "../../context/UserContext";
import { useState } from "react";
import { formatLettresDate } from "../../utils/ClasseEvaluations";
import useUpdateGroupe from "../../hooks/update_api_hook/useUpdateGroupe";
import { url } from "../../utils/confserveur";
import { usePostApi } from "../../hooks/post_api_hook/usePostApi";
import { Calendar, Trash2, Edit3, Save, X, AlertCircle, Clock } from "lucide-react";

import useConfirm from "../../hooks/ui/useConfirm";

const ListPeriode = () => {
  const { etablissementData, refreshEtablissementData } = useUser();
  const { confirm, ConfirmDialog } = useConfirm();
  const [error, setError] = useState(null);
  const { updateGroupe } = useUpdateGroupe();
  const [, doPost] = usePostApi(url + "periodescolaires/delete-all/");

  // Regroupement des périodes par type (trimestre ou semestre)
  const periodsByType = {
    trimestre: [],
    semestre: [],
  };

  if (etablissementData && etablissementData.periodes_scolaires) {
    etablissementData.periodes_scolaires.forEach((periode) => {
      if (periode.type_periode === "trimestre") {
        periodsByType.trimestre.push(periode);
      } else if (periode.type_periode === "semestre") {
        periodsByType.semestre.push(periode);
      }
    });
  }

  // Fonction de validation des périodes pour un type donné
  const validatePeriods = (periods, type) => {
    const validationErrors = [];
    periods.forEach((periode, index) => {
      if (new Date(periode.date_debut) >= new Date(periode.date_fin)) {
        validationErrors.push(
          `${type} ${periode.numero}: la date de début doit être inférieure à la date de fin.`
        );
      }
      if (index > 0) {
        const prevPeriode = periods[index - 1];
        if (new Date(periode.date_debut) <= new Date(prevPeriode.date_fin)) {
          validationErrors.push(
            `${type} ${periode.numero}: la date de début doit être postérieure à la date de fin du ${type} ${prevPeriode.numero}.`
          );
        }
      }
    });
    return validationErrors;
  };

  // Fonction pour supprimer toutes les périodes
  const handleDeleteAll = async () => {
    if (await confirm({ title: "Supprimer toutes les périodes ?", message: "Voulez-vous vraiment supprimer toutes les périodes ?", confirmLabel: "Supprimer", variant: "danger" })) {
      try {
        await doPost({ etablissement: etablissementData.id })
        if (refreshEtablissementData) {
          refreshEtablissementData();
        }
      } catch (err) {
        console.error(err);
        setError("Erreur lors de la suppression des périodes.");
      }
    }
  };

  // Fonction pour gérer la sauvegarde d'une période modifiée
  const handleSavePeriod = async (periode, newStart, newEnd) => {
    try {
      await updateGroupe(`${url}periodescolaires/${periode.id}/`, {
        date_debut: newStart,
        date_fin: newEnd,
      });
    } catch (error) {
      console.error("Erreur lors de la mise à jour de l'événement : ", error);
    }
    if (refreshEtablissementData) {
      refreshEtablissementData();
    }
  };

  // Couleurs du thème (bleu)
  const colors = {
    gradient: 'from-blue-600 via-cyan-500 to-sky-400',
    blob1: '#67e8f9',
    blob2: '#a5f3fc',
  };

  // Compter le total des périodes
  const totalPeriodes = periodsByType.trimestre.length + periodsByType.semestre.length;

  return (
    <div className="h-fit  w-full flex justify-center bg-gradient-to-br py-6 xl:py-4 2xl:py-5 3xl:py-6">
      {ConfirmDialog}
      <div className="w-fit bg-white rounded-3xl shadow-xl pb-6 xl:pb-4 2xl:pb-5 3xl:pb-6 overflow-hidden">
        <div className="">

          {/* Header moderne avec gradient et blobs animés */}
          <div className="relative mb-8 xl:mb-5 2xl:mb-6 3xl:mb-8 overflow-hidden rounded-t-2xl sm:rounded-t-3xl shadow-2xl">
            {/* Glow effect behind header */}
            <div className={`absolute -inset-2 bg-gradient-to-r ${colors.gradient} rounded-3xl blur-2xl opacity-30 animate-pulse`} />

            <div className="relative overflow-hidden">
              {/* Background gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${colors.gradient}`} />

              {/* Animated liquid blobs */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 800 200" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="header-blob1-periode" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={colors.blob1} stopOpacity="0.5" />
                    <stop offset="100%" stopColor={colors.blob2} stopOpacity="0.2" />
                  </linearGradient>
                  <linearGradient id="header-blob2-periode" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor={colors.blob2} stopOpacity="0.4" />
                    <stop offset="100%" stopColor={colors.blob1} stopOpacity="0.1" />
                  </linearGradient>
                </defs>

                {/* First animated blob */}
                <path fill="url(#header-blob1-periode)">
                  <animate attributeName="d" dur="8s" repeatCount="indefinite"
                    values="M0,100 Q200,40 400,80 Q600,130 800,70 L800,200 L0,200 Z;
                            M0,70 Q200,110 400,50 Q600,80 800,120 L800,200 L0,200 Z;
                            M0,100 Q200,40 400,80 Q600,130 800,70 L800,200 L0,200 Z" />
                </path>

                {/* Second animated blob */}
                <path fill="url(#header-blob2-periode)">
                  <animate attributeName="d" dur="6s" repeatCount="indefinite"
                    values="M0,130 Q300,80 500,110 Q700,140 800,90 L800,200 L0,200 Z;
                            M0,90 Q300,130 500,80 Q700,100 800,140 L800,200 L0,200 Z;
                            M0,130 Q300,80 500,110 Q700,140 800,90 L800,200 L0,200 Z" />
                </path>

                {/* Floating particles */}
                <circle cx="150" cy="80" r="3" fill="white" opacity="0.4">
                  <animate attributeName="cy" dur="3s" values="80;50;80" repeatCount="indefinite" />
                </circle>
                <circle cx="650" cy="100" r="2.5" fill="white" opacity="0.3">
                  <animate attributeName="cy" dur="4s" values="100;60;100" repeatCount="indefinite" />
                </circle>
                <circle cx="400" cy="70" r="3" fill="white" opacity="0.35">
                  <animate attributeName="cy" dur="3.5s" values="70;40;70" repeatCount="indefinite" />
                </circle>
              </svg>

              {/* Content layer */}
              <div className="relative px-6 sm:px-8 xl:px-5 2xl:px-6 3xl:px-8 py-8 sm:py-10 xl:py-6 2xl:py-8 3xl:py-10 text-center">
                {/* Icon */}
                <div className="flex justify-center mb-4 xl:mb-3 2xl:mb-3.5 3xl:mb-4">
                  <div className="p-3 sm:p-4 xl:p-2.5 2xl:p-3 3xl:p-4 bg-white/20 backdrop-blur rounded-2xl border border-white/30 shadow-xl">
                    <Calendar className="w-8 h-8 sm:w-10 sm:h-10 xl:w-7 xl:h-7 2xl:w-8 2xl:h-8 3xl:w-10 3xl:h-10 text-white" />
                  </div>
                </div>

                {/* Title */}
                <h1 className="text-2xl sm:text-3xl md:text-4xl xl:text-2xl 2xl:text-3xl 3xl:text-4xl font-bold text-white mb-2 xl:mb-1.5 2xl:mb-1.5 3xl:mb-2">
                  Périodes Scolaires
                </h1>
                <p className="text-white/80 text-sm sm:text-base md:text-lg xl:text-sm 2xl:text-base 3xl:text-lg">
                  {etablissementData?.nom || 'Votre établissement'} • {totalPeriodes} période{totalPeriodes > 1 ? 's' : ''}
                </p>
              </div>
            </div>
          </div>

          {/* Message d'erreur */}
          {error && (
            <div className="px-4 mb-6">
              <div className="relative">
                <div className="absolute -inset-0.5 bg-gradient-to-r from-red-300 to-rose-300 rounded-2xl blur opacity-20" />
                <div className="relative bg-red-50/80 backdrop-blur-xl border border-red-200/50 rounded-2xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-gradient-to-br from-red-500 to-rose-500 rounded-lg flex-shrink-0">
                      <AlertCircle className="w-4 h-4 text-white" />
                    </div>
                    <div className="text-sm text-red-800">
                      <p className="font-semibold">Erreur</p>
                      <p className="text-red-700">{error}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Statistiques */}
          <div className="px-4 xl:px-3 2xl:px-3.5 3xl:px-4 grid grid-cols-1 md:grid-cols-2 gap-4 xl:gap-3 2xl:gap-3.5 3xl:gap-4 mb-6 xl:mb-4 2xl:mb-5 3xl:mb-6">
            {/* Stat Trimestres */}
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl blur opacity-20 group-hover:opacity-30 transition duration-300" />
              <div className="relative bg-white/70 backdrop-blur-xl border border-white/50 rounded-2xl p-5 xl:p-3.5 2xl:p-4 3xl:p-5 shadow-lg">
                <div className="flex items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                  <div className="p-3 xl:p-2 2xl:p-2.5 3xl:p-3 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl shadow-lg shadow-blue-500/30">
                    <Clock className="w-5 h-5 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-slate-500 font-medium">Trimestres</p>
                    <p className="text-2xl xl:text-lg 2xl:text-xl 3xl:text-2xl font-bold text-slate-800">{periodsByType.trimestre.length}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Stat Semestres */}
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl blur opacity-20 group-hover:opacity-30 transition duration-300" />
              <div className="relative bg-white/70 backdrop-blur-xl border border-white/50 rounded-2xl p-5 xl:p-3.5 2xl:p-4 3xl:p-5 shadow-lg">
                <div className="flex items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3">
                  <div className="p-3 xl:p-2 2xl:p-2.5 3xl:p-3 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl shadow-lg shadow-blue-500/30">
                    <Calendar className="w-5 h-5 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm text-slate-500 font-medium">Semestres</p>
                    <p className="text-2xl xl:text-lg 2xl:text-xl 3xl:text-2xl font-bold text-slate-800">{periodsByType.semestre.length}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Liste des périodes */}
          <div className="px-4 xl:px-3 2xl:px-3.5 3xl:px-4">
            {Object.keys(periodsByType).map((type) =>
              periodsByType[type].length > 0 && (
                <div key={type} className="mb-6 xl:mb-4 2xl:mb-5 3xl:mb-6">
                  <h3 className="text-xl xl:text-base 2xl:text-lg 3xl:text-xl font-bold text-slate-800 mb-4 xl:mb-3 2xl:mb-3.5 3xl:mb-4 flex items-center gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2">
                    <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"></span>
                    {type === "trimestre" ? "Trimestres" : "Semestres"}
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 xl:gap-3 2xl:gap-3.5 3xl:gap-4">
                    {periodsByType[type].map((periode, index) => (
                      <PeriodItem
                        key={periode.id || index}
                        periode={periode}
                        type={type}
                        onSave={handleSavePeriod}
                        allPeriods={periodsByType[type]}
                        index={index}
                      />
                    ))}
                  </div>

                  {validatePeriods(periodsByType[type], type).length > 0 && (
                    <div className="mt-4 relative">
                      <div className="absolute -inset-0.5 bg-gradient-to-r from-amber-300 to-orange-300 rounded-2xl blur opacity-20" />
                      <div className="relative bg-amber-50/80 backdrop-blur-xl border border-amber-200/50 rounded-2xl p-4">
                        <div className="flex items-start gap-3">
                          <div className="p-2 bg-gradient-to-br from-amber-500 to-orange-500 rounded-lg flex-shrink-0">
                            <AlertCircle className="w-4 h-4 text-white" />
                          </div>
                          <div className="text-sm text-amber-800">
                            <p className="font-semibold mb-1">Erreurs de validation</p>
                            <ul className="text-amber-700 list-disc pl-4 space-y-1">
                              {validatePeriods(periodsByType[type], type).map((errMsg, idx) => (
                                <li key={idx}>{errMsg}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )
            )}
          </div>

          {/* Bouton supprimer toutes les périodes */}
          <div className="px-4 xl:px-3 2xl:px-3.5 3xl:px-4 mt-6 xl:mt-4 2xl:mt-5 3xl:mt-6">
            <button
              onClick={handleDeleteAll}
              className="w-full flex items-center justify-center gap-2 xl:gap-1.5 2xl:gap-1.5 3xl:gap-2 px-6 xl:px-4 2xl:px-5 3xl:px-6 py-3 xl:py-2 2xl:py-2.5 3xl:py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl hover:from-orange-600 hover:to-amber-600 transition-all duration-200 shadow-lg shadow-orange-500/30 hover:shadow-xl hover:shadow-orange-500/40 font-medium xl:text-sm 2xl:text-sm 3xl:text-base"
            >
              <Trash2 className="w-5 h-5 xl:w-4 xl:h-4 2xl:w-[18px] 2xl:h-[18px] 3xl:w-5 3xl:h-5" />
              Supprimer toutes les périodes
            </button>
          </div>

          {/* Note d'information */}
          <div className="px-4 xl:px-3 2xl:px-3.5 3xl:px-4 mt-6 xl:mt-4 2xl:mt-5 3xl:mt-6 relative">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-300 to-cyan-300 rounded-2xl blur opacity-20" />
            <div className="relative bg-blue-50/80 backdrop-blur-xl border border-blue-200/50 rounded-2xl p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex-shrink-0">
                  <AlertCircle className="w-4 h-4 text-white" />
                </div>
                <div className="text-sm text-blue-800">
                  <p className="font-semibold mb-1">Information</p>
                  <p className="text-blue-700">Les périodes scolaires définissent le découpage de l'année pour les évaluations. Vous pouvez modifier les dates en cliquant sur le bouton "Modifier".</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};


// Composant pour une période individuelle avec mode édition
const PeriodItem = ({ periode, type, onSave, allPeriods, index }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [newStart, setNewStart] = useState(periode.date_debut);
  const [newEnd, setNewEnd] = useState(periode.date_fin);
  const [validationError, setValidationError] = useState(null);

  // Validation des dates
  const validateDates = (start, end) => {
    // Vérifier que la date de début est inférieure à la date de fin
    if (new Date(start) >= new Date(end)) {
      return `La date de début doit être inférieure à la date de fin.`;
    }

    // Vérifier que la date de début est postérieure à la fin de la période précédente
    if (index > 0) {
      const prevPeriode = allPeriods[index - 1];
      if (new Date(start) <= new Date(prevPeriode.date_fin)) {
        return `La date de début doit être postérieure à la fin du ${type} ${prevPeriode.numero} (${prevPeriode.date_fin}).`;
      }
    }

    // Vérifier que la date de fin est antérieure au début de la période suivante
    if (index < allPeriods.length - 1) {
      const nextPeriode = allPeriods[index + 1];
      if (new Date(end) >= new Date(nextPeriode.date_debut)) {
        return `La date de fin doit être antérieure au début du ${type} ${nextPeriode.numero} (${nextPeriode.date_debut}).`;
      }
    }

    return null;
  };

  const handleSave = () => {
    const error = validateDates(newStart, newEnd);
    if (error) {
      setValidationError(error);
      return;
    }

    setValidationError(null);
    if (onSave) {
      onSave(periode, newStart, newEnd);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setNewStart(periode.date_debut);
    setNewEnd(periode.date_fin);
    setValidationError(null);
    setIsEditing(false);
  };

  return (
    <div className="relative group">
      {/* Glow effect */}
      <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-2xl blur opacity-0 group-hover:opacity-20 transition duration-300" />

      {/* Card avec glassmorphism */}
      <div className="relative bg-white/70 backdrop-blur-xl border border-white/50 rounded-2xl p-5 xl:p-3.5 2xl:p-4 3xl:p-5 shadow-lg hover:shadow-xl transition-all duration-300">
        {/* Icône modifier en haut à droite (visible uniquement en mode lecture) */}
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="absolute top-3 right-3 p-2 bg-yellow-500 hover:bg-yellow-600 rounded-lg text-white transition-all duration-200 opacity-0 group-hover:opacity-100"
            title="Modifier"
          >
            <Edit3 className="w-4 h-4" />
          </button>
        )}

        {/* Header de la card */}
        <div className="flex items-center gap-3 xl:gap-2 2xl:gap-2.5 3xl:gap-3 mb-4 xl:mb-3 2xl:mb-3.5 3xl:mb-4">
          <div className="w-12 h-12 xl:w-9 xl:h-9 2xl:w-10 2xl:h-10 3xl:w-12 3xl:h-12 rounded-xl flex items-center justify-center shadow-lg bg-gradient-to-br from-blue-500 to-cyan-500 shadow-blue-500/30">
            <span className="text-white font-bold text-lg xl:text-sm 2xl:text-base 3xl:text-lg">{periode.numero}</span>
          </div>
          <div>
            <p className="font-bold text-slate-800 text-lg xl:text-sm 2xl:text-base 3xl:text-lg">
              {type === "trimestre" ? `Trimestre ${periode.numero}` : `Semestre ${periode.numero}`}
            </p>
          </div>
        </div>

        {isEditing ? (
          <div className="space-y-3 xl:space-y-2 2xl:space-y-2.5 3xl:space-y-3">
            {/* Message d'erreur de validation */}
            {validationError && (
              <div className="p-3 xl:p-2 2xl:p-2.5 3xl:p-3 bg-red-50 border border-red-200 rounded-xl">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-red-700">{validationError}</p>
                </div>
              </div>
            )}
            <div>
              <label className="block text-xs text-slate-500 uppercase tracking-wide mb-1">Date de début</label>
              <input
                type="date"
                value={newStart}
                onChange={(e) => {
                  setNewStart(e.target.value);
                  setValidationError(null);
                }}
                className="w-full px-3 py-2 bg-white/50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-300 transition-all text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-slate-500 uppercase tracking-wide mb-1">Date de fin</label>
              <input
                type="date"
                value={newEnd}
                onChange={(e) => {
                  setNewEnd(e.target.value);
                  setValidationError(null);
                }}
                className="w-full px-3 py-2 bg-white/50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-300 transition-all text-sm"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <button
                onClick={handleSave}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gradient-to-r from-emerald-500 to-green-500 text-white rounded-xl text-sm font-medium shadow-lg shadow-emerald-500/30 hover:shadow-xl hover:shadow-emerald-500/40 transition-all"
              >
                <Save className="w-4 h-4" />
                Sauvegarder
              </button>
              <button
                onClick={handleCancel}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-slate-100 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-200 transition-all"
              >
                <X className="w-4 h-4" />
                Annuler
              </button>
            </div>
          </div>
        ) : (
          /* Dates en mode lecture */
          <div className="space-y-2 xl:space-y-1.5 2xl:space-y-1.5 3xl:space-y-2">
            <div className="flex items-center justify-between p-2 xl:p-1.5 2xl:p-1.5 3xl:p-2 bg-slate-50/80 rounded-lg">
              <span className="text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs text-slate-500">Début</span>
              <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium text-slate-700">{formatLettresDate(periode.date_debut)}</span>
            </div>
            <div className="flex items-center justify-between p-2 xl:p-1.5 2xl:p-1.5 3xl:p-2 bg-slate-50/80 rounded-lg">
              <span className="text-xs xl:text-[10px] 2xl:text-[11px] 3xl:text-xs text-slate-500">Fin</span>
              <span className="text-sm xl:text-xs 2xl:text-xs 3xl:text-sm font-medium text-slate-700">{formatLettresDate(periode.date_fin)}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


export default ListPeriode;
